import type { ChunkRef } from "daedalus-core";
import { fogCells, isExplored, markVision, newExploredSet, newVisionRange, visionCells, type ExploredSet, type MapPlaces, type Size } from "odyssey-shared";
import type { WorldMapReport } from "../report.ts";
import { localStore, readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import { say } from "../../words.ts";
import type { FogCell } from "./fog.ts";
import { MAP_SCALE, edgeMark, panOrigin, viewOrigin, type EdgeMark, type Origin, type TilePoint } from "./geometry.ts";
import { canZoomIn, canZoomOut, openLevel, regionReady, resetLevel, zoomIn, zoomOut, type LevelState, type MapLevel } from "./levels.ts";
import { NO_PARTY, type PartyOnMap } from "../party/positions.ts";
import { PLACE_KINDS, awayEntries, directionOf, measured, nearestFirst, partyKey, partyMarks, placeLine, placeMarks, type KnownPlace, type PlaceMark } from "./places.ts";
import { chooseNames, markSize, type NameCandidate } from "./labels.ts";
import { buildPanel, type PanelModel } from "./panel.ts";
import { PRESENTATION_KEY, dockedState, floatedState, formatPresentation, openedState, parsePresentation, poppedInState, poppedOutState, shownPresentation, type MapPresentation, type PresentationState } from "./presentation.ts";
import { parsePin, pinAt, pinKey, pinReached, serialisePin } from "./pin.ts";
import type { WorldMapSession } from "./session.ts";

/**
 * The world map's store (P2.12-T6, ADR-100; ADR-061 decision 1: a DOM frame with canvas contents), owned by the game
 * rather than by React. It holds whether the window is open, the map's picture and the page's own copy of exploration,
 * the places the server sent, the pin, and where the view stands, and it tells its listeners only when a value the
 * window shows changes, so walking redraws nothing until the arrow, the list's words or the view move a pixel. The scene
 * feeds it the character's position every frame; it draws the picture only while the window is open and one chunk a
 * frame (`frame`), and counts what it draws and how long the chunks took, which the report and the measurement of
 * decision 3 read.
 */

/**
 * The picture as the store uses it: `WorldMapPicture` answers it, and a test stands in.
 */
export interface WorldMapPort {
  readonly version: number;
  readonly pending: number;
  readonly incomplete: number;
  readonly chunks: number;
  readonly source: CanvasImageSource | null;
  readonly size: Size;
  readonly drawnOrder: readonly ChunkRef[];
  start(from: ChunkRef): boolean;
  step(): number;
  retry(): number;
  setExplored(set: ExploredSet | null): void;
  explore(cells: readonly FogCell[]): void;
  dispose(): void;
}

/**
 * The map the page stands on: its ids, its name, its size in tiles, its chunk size and the picture of it.
 */
export interface WorldMapWorld {
  readonly worldId: string;
  readonly mapId: string;
  readonly title: string;
  readonly width: number;
  readonly height: number;
  readonly chunkSize: number;
  readonly picture: WorldMapPort;
}

/**
 * The storage key the places panel's open or shut state is kept under, one for every world and character on the device.
 */
export const PLACES_PANEL_KEY = "odyssey.worldmap.places";

/**
 * The longest filter the panel keeps, in characters.
 */
export const FILTER_LENGTH = 48;

/**
 * What the window reads.
 */
export interface WorldMapSnapshot {
  readonly open: boolean;
  readonly ready: boolean;
  readonly presentation: MapPresentation;
  readonly level: MapLevel;
  readonly step: number;
  readonly canZoomIn: boolean;
  readonly canZoomOut: boolean;
  readonly regionReady: boolean;
  readonly title: string;
  readonly heading: string;
  readonly mapLabel: string;
  readonly following: boolean;
  readonly drawing: boolean;
  readonly view: Size;
  readonly origin: Origin;
  readonly you: { readonly x: number; readonly y: number; readonly heading: number } | null;
  readonly youEdge: EdgeMark | null;
  readonly marks: readonly PlaceMark[];
  readonly named: readonly string[];
  readonly count: number;
  readonly panelOpen: boolean;
  readonly filter: string;
  readonly panel: PanelModel<KnownPlace> | null;
  readonly pin: TilePoint | null;
  readonly pinLine: string | null;
}

/**
 * What the window reads, and a way to hear that it changed.
 */
export interface WorldMapSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => WorldMapSnapshot;
}

/**
 * What the measurement of ADR-100 decision 3 records: chunks drawn, the frames that drew one, the milliseconds spent
 * drawing, the longest single frame, the time from the first chunk's start to the last one's end, and whether every
 * chunk is drawn.
 */
export interface WorldMapMeasure {
  readonly chunks: number;
  readonly frames: number;
  readonly drawMs: number;
  readonly longestMs: number;
  readonly spanMs: number;
  readonly done: boolean;
}

/**
 * The Nearby level's step as the minimap keeps it (ADR-100 decision 41): one value the minimap's zoom buttons and the
 * Map's move together, kept on the device by the minimap's store, so the Map keeps nothing of its own.
 */
export interface NearbyPort {
  readonly step: () => number;
  readonly setStep: (step: number) => void;
  readonly subscribe: (listener: () => void) => () => void;
}

/**
 * What the store is made of: where the pin is kept, whose pin it is, the clock the chunks are timed by, and the
 * minimap's step that gives the Map its Nearby level; a store with none opens at Region, as the window did before the
 * levels.
 */
export interface WorldMapOptions {
  readonly storage?: SettingsStore | null;
  readonly character?: () => string;
  readonly now?: () => number;
  readonly nearby?: NearbyPort;
}

/**
 * The inputs a snapshot was last built from, the position in sixteenths of a tile and the heading in eighths.
 */
interface Stamp {
  readonly world: WorldMapWorld | null;
  readonly opened: boolean;
  readonly presentation: MapPresentation;
  readonly started: boolean;
  readonly drawing: boolean;
  readonly ready: boolean;
  readonly level: MapLevel;
  readonly step: number;
  readonly view: Size;
  readonly panned: Origin | null;
  readonly x: number;
  readonly y: number;
  readonly heading: number;
  readonly placed: boolean;
  readonly places: MapPlaces | null;
  readonly party: PartyOnMap;
  readonly pin: TilePoint | null;
  readonly panelOpen: boolean;
  readonly filter: string;
  readonly hover: string | null;
  readonly focus: string | null;
}

const NO_MARKS: readonly PlaceMark[] = [];
const NO_NAMES: readonly string[] = [];
const EMPTY_VIEW: Size = { width: 466, height: 256 };

/**
 * Whether two lists of keys are the same keys in the same order.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when they match.
 */
function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  return a === b || (a.length === b.length && a.every((key, index) => key === b[index]));
}

/**
 * Whether two arrows read the same.
 *
 * @param a One arrow or null.
 * @param b The other.
 * @returns True when both are null or at the same place and angle.
 */
function sameEdge(a: EdgeMark | null, b: EdgeMark | null): boolean {
  return a === b || (a !== null && b !== null && a.x === b.x && a.y === b.y && Math.abs(a.angle - b.angle) < 0.02);
}

/**
 * Whether two points read the same.
 *
 * @param a One point or null.
 * @param b The other.
 * @returns True when both are null or equal.
 */
function samePoint(a: TilePoint | null, b: TilePoint | null): boolean {
  return a === b || (a !== null && b !== null && a.x === b.x && a.y === b.y);
}

/**
 * Whether two snapshots read the same, the character's place compared to the pixel it is drawn at.
 *
 * @param a One snapshot.
 * @param b The other.
 * @returns True when nothing the window shows differs.
 */
function sameSnapshot(a: WorldMapSnapshot, b: WorldMapSnapshot): boolean {
  const you = (value: WorldMapSnapshot["you"]): string => (value === null ? "" : `${Math.round(value.x * MAP_SCALE)},${Math.round(value.y * MAP_SCALE)},${Math.round(value.heading * 8)}`);
  return a.open === b.open && a.ready === b.ready && a.presentation === b.presentation && a.level === b.level && a.step === b.step && a.canZoomIn === b.canZoomIn && a.canZoomOut === b.canZoomOut && a.regionReady === b.regionReady && a.title === b.title && a.heading === b.heading && a.following === b.following && a.drawing === b.drawing && a.view.width === b.view.width && a.view.height === b.view.height && a.origin.left === b.origin.left && a.origin.top === b.origin.top && you(a.you) === you(b.you) && sameEdge(a.youEdge, b.youEdge) && a.marks === b.marks && sameKeys(a.named, b.named) && a.count === b.count && a.panelOpen === b.panelOpen && a.filter === b.filter && a.panel === b.panel && samePoint(a.pin, b.pin) && a.pinLine === b.pinLine;
}

/**
 * The world map's store.
 */
export class WorldMapStore implements WorldMapSource, WorldMapSession {
  private readonly storage: SettingsStore | null;
  private readonly character: () => string;
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();
  private readonly drawers = new Set<() => void>();
  private world: WorldMapWorld | null = null;
  private opened = false;
  private presented: PresentationState;
  private phone = false;
  private readonly forwarders = new Set<() => void>();
  private started = false;
  private readonly nearby: NearbyPort | null;
  private levels: LevelState;
  private view: Size = EMPTY_VIEW;
  private px = 0;
  private py = 0;
  private heading = 0;
  private placed = false;
  private panned: Origin | null = null;
  private received: ExploredSet | null = null;
  private explored: ExploredSet | null = null;
  private places: MapPlaces | null = null;
  private readonly placeListeners = new Set<(places: MapPlaces | null) => void>();
  private pin: TilePoint | null = null;
  private panelOpen: boolean;
  private filter = "";
  private hover: string | null = null;
  private focus: string | null = null;
  private party: PartyOnMap = NO_PARTY;
  private markFor: MapPlaces | null = null;
  private markParty: PartyOnMap = NO_PARTY;
  private alwaysNamed: ReadonlySet<string> = new Set();
  private markCache: readonly PlaceMark[] = [];
  private candidateCache: readonly NameCandidate[] = [];
  private panelCache: { readonly places: MapPlaces | null; readonly party: PartyOnMap; readonly filter: string; readonly tx: number; readonly ty: number; readonly model: PanelModel<KnownPlace> } | null = null;
  private builds = 0;
  private pinFor: string | null = null;
  private pinWorld = "";
  private pinMap = "";
  private pinCharacter = "";
  private stamp: Stamp | null = null;
  private readonly last = newVisionRange();
  private readonly next = newVisionRange();
  private drawnVersion = -1;
  private drawnOrigin: Origin = { left: -1, top: -1 };
  private drawCount = 0;
  private chunksDrawn = 0;
  private drawFrames = 0;
  private drawMs = 0;
  private longestMs = 0;
  private firstAt = -1;
  private lastAt = -1;
  private waitFrom: number | null = null;
  private waitMs = -1;
  private snapshot: WorldMapSnapshot;

  /**
   * @param options Where the pin is kept (this browser's `localStorage` when omitted), whose it is, the clock, and the
   *   minimap's step when the Map has a Nearby level.
   */
  public constructor(options: WorldMapOptions = {}) {
    this.nearby = options.nearby ?? null;
    this.levels = this.opening();
    this.nearby?.subscribe(() => this.syncStep());
    this.storage = options.storage === undefined ? localStore() : options.storage;
    this.character = options.character ?? ((): string => "dev");
    this.now = options.now ?? ((): number => performance.now());
    this.panelOpen = readSetting(PLACES_PANEL_KEY, (raw) => raw === "open", this.storage);
    this.presented = openedState(readSetting(PRESENTATION_KEY, parsePresentation, this.storage));
    this.snapshot = this.build();
  }

  /**
   * Listens for the snapshot changing; the same function for the store's life, so React subscribes once.
   *
   * @param listener Called after each change.
   * @returns Stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The current snapshot, kept until a value changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): WorldMapSnapshot => this.snapshot;

  /**
   * Listens for the view's canvas needing a repaint: a chunk or a cell drawn, or the view moved.
   *
   * @param listener Called each time.
   * @returns Stops listening.
   */
  public onDraw(listener: () => void): () => void {
    this.drawers.add(listener);
    return () => {
      this.drawers.delete(listener);
    };
  }

  /**
   * How many times the view was asked to repaint, which a page standing still never raises.
   *
   * @returns The count.
   */
  public get draws(): number {
    return this.drawCount;
  }

  /**
   * The picture the view draws from, or null before the picture is made or started.
   *
   * @returns The source.
   */
  public get source(): CanvasImageSource | null {
    return this.world?.picture.source ?? null;
  }

  /**
   * The picture's size in pixels.
   *
   * @returns The size.
   */
  public get pictureSize(): Size {
    return this.world?.picture.size ?? { width: 0, height: 0 };
  }

  /**
   * What drawing the map has cost so far (decision 3's measurement).
   *
   * @returns The measure.
   */
  public measure(): WorldMapMeasure {
    const world = this.world;
    return { chunks: this.chunksDrawn, frames: this.drawFrames, drawMs: this.drawMs, longestMs: this.longestMs, spanMs: this.firstAt < 0 ? 0 : this.lastAt - this.firstAt, done: world !== null && this.started && world.picture.pending === 0 && this.chunksDrawn > 0 };
  }

  /**
   * The state the browser tests and the measurement read, built when asked.
   *
   * @returns The report.
   */
  public report(): WorldMapReport {
    const measure = this.measure();
    const world = this.world;
    const set = this.explored;
    let explored = 0;
    if (set !== null) {
      for (let row = 0; row < set.rows; row += 1) {
        for (let column = 0; column < set.columns; column += 1) {
          if (isExplored(set, column, row)) {
            explored += 1;
          }
        }
      }
    }
    this.ensurePin();
    return { open: this.opened, ready: this.world !== null, presentation: this.shownNow(), level: this.levels.level, step: this.levels.step, drawing: this.snapshot.drawing, draws: this.drawCount, chunks: measure.chunks, frames: measure.frames, drawMs: measure.drawMs, longestMs: measure.longestMs, spanMs: measure.spanMs, done: measure.done, places: this.lines(), pin: this.pin, following: this.panned === null, origin: this.currentOrigin(), explored, order: world === null ? [] : world.picture.drawnOrder.map((chunk) => `${chunk.cx},${chunk.cy}`), regionWaitMs: this.waitMs };
  }

  /**
   * Takes the map the page stands on, and the picture of it, ending the picture of the map before. The exploration the
   * server sent before the map was known is given to the picture, and the pin kept for this character is read.
   *
   * @param world The map.
   */
  public setWorld(world: WorldMapWorld): void {
    this.world?.picture.dispose();
    this.world = world;
    this.party = NO_PARTY;
    this.started = false;
    this.panned = null;
    this.pinFor = null;
    this.stamp = null;
    this.chunksDrawn = 0;
    this.drawFrames = 0;
    this.drawMs = 0;
    this.longestMs = 0;
    this.firstAt = -1;
    this.lastAt = -1;
    this.waitFrom = null;
    this.waitMs = -1;
    this.drawnVersion = -1;
    this.adopt(this.received);
    this.last.fill(0);
    this.refresh();
  }

  /**
   * Forgets the map, as when the scene ends.
   */
  public clearWorld(): void {
    this.world?.picture.dispose();
    this.world = null;
    this.started = false;
    this.refresh();
  }

  /**
   * Sets the size of the view in logical pixels, which the window's layout decides.
   *
   * @param view The size.
   */
  public setView(view: Size): void {
    if (view.width === this.view.width && view.height === this.view.height) {
      return;
    }
    this.view = view;
    this.refresh();
  }

  /**
   * Takes a `mapPlaces` message, which replaces the places held.
   *
   * @param places The message's fields.
   */
  public receivePlaces(places: MapPlaces): void {
    this.places = places;
    this.tellPlaces();
    this.refresh();
  }

  /**
   * Takes the party as the page holds it (ADR-100 decisions 10 to 14): the members on this map become marks in their
   * colours with their first names and rows of the Party group, a member on another map a row with no position. A party
   * that reads the same changes nothing, so a frame of a party standing still draws nothing.
   *
   * @param party The members on the map and the members away.
   */
  public setParty(party: PartyOnMap): void {
    if (party === this.party) {
      return;
    }
    this.party = party;
    this.refresh();
  }

  /**
   * Listens to the places the page holds: told at once of those already held, then at every `mapPlaces` and, with null,
   * when the session's places are forgotten. The pictures read the shrine art from them (ADR-100 decision 58).
   *
   * @param listener Told the places, or null.
   * @returns A function that stops the listening.
   */
  public onPlaces(listener: (places: MapPlaces | null) => void): () => void {
    this.placeListeners.add(listener);
    if (this.places !== null) {
      listener(this.places);
    }
    return () => {
      this.placeListeners.delete(listener);
    };
  }

  /**
   * Tells every listener the places held.
   */
  private tellPlaces(): void {
    for (const listener of [...this.placeListeners]) {
      listener(this.places);
    }
  }

  /**
   * Takes the join's `explored` set. A set of other counts than the map's is drawn as nothing explored.
   *
   * @param set The set.
   */
  public receiveExplored(set: ExploredSet): void {
    this.received = set;
    this.adopt(set);
    this.refresh();
  }

  /**
   * Forgets the session's places and exploration; the window stays as it is.
   */
  public reset(): void {
    this.places = null;
    this.party = NO_PARTY;
    this.tellPlaces();
    this.received = null;
    this.hover = null;
    this.focus = null;
    this.pin = null;
    this.pinFor = null;
    this.stamp = null;
    this.adopt(null);
    this.last.fill(0);
    this.refresh();
  }

  /**
   * Opens the window on the player at Nearby, at the step the minimap keeps (ADR-100 decision 40), or at Region when the
   * Map has no Nearby level. Opening it while it is open changes nothing, so a level is kept within an opening. The
   * Region picture is started now at the latest, the chunk under the character first (P2.12-T32), and is drawn by
   * `prefetch` in idle time, or by `frame` once Region is shown.
   */
  public open(): void {
    if (this.opened) {
      this.bringForward();
      return;
    }
    this.opened = true;
    this.presented = openedState(this.presented.kept);
    this.levels = this.nearby === null ? this.opening() : resetLevel({ ...this.levels, step: this.nearby.step() });
    this.panned = null;
    this.showRegion();
    this.refresh();
    this.drawView();
  }

  /**
   * The state of a Map just opened: Nearby on the player at the minimap's step, or Region where there is no Nearby.
   *
   * @returns The state.
   */
  private opening(): LevelState {
    return this.nearby === null ? { level: "region", step: 1, follow: true, regionShown: true } : openLevel(this.nearby.step());
  }

  /**
   * Starts the picture the first time it is wanted (an idle prefetch or the Map's open), the chunk under the character
   * first, and queues again the chunks that lacked an atlas page on a later showing.
   */
  private showRegion(): void {
    const world = this.world;
    if (world === null) {
      return;
    }
    if (!this.started) {
      this.started = true;
      world.picture.start(this.chunkUnder(world));
    } else if (world.picture.incomplete > 0) {
      world.picture.retry();
    }
  }

  /**
   * Draws one chunk of the Region picture ahead of its being looked at (P2.12-T32, ADR-100's note under decision 42),
   * starting the picture at the chunk under the character the first time. The scene's idle drawer calls it; a frame never
   * does. The chunk is counted and timed as the frame's are. Until the character has a place nothing is started, so the
   * first chunk drawn is the one under the character and not the map's middle.
   *
   * @returns True while more chunks are owed, false when all are drawn, no map is held, or the picture could not be made.
   * @throws Error whatever the chunk's feed or paint throws; the chunk is then left for `retry`.
   */
  public prefetch(): boolean {
    const world = this.world;
    if (world === null) {
      return false;
    }
    if (!this.placed && !this.started) {
      return true;
    }
    this.showRegion();
    this.drawChunk(world);
    this.noteShown();
    this.refresh();
    this.drawView();
    return world.picture.pending > 0;
  }

  /**
   * Draws the next chunk of the picture, timed.
   *
   * @param world The map.
   */
  private drawChunk(world: WorldMapWorld): void {
    if (world.picture.pending === 0) {
      return;
    }
    const began = this.now();
    let composed = 0;
    try {
      composed = world.picture.step();
    } finally {
      const ended = this.now();
      if (composed > 0) {
        const spent = ended - began;
        if (this.firstAt < 0) {
          this.firstAt = began;
        }
        this.lastAt = ended;
        this.chunksDrawn += composed;
        this.drawFrames += 1;
        this.drawMs += spent;
        this.longestMs = Math.max(this.longestMs, spent);
      }
    }
  }

  /**
   * Whether Region has a picture to show.
   *
   * @returns True when it is started and its first chunk is drawn, or it owes nothing.
   */
  private regionIsReady(): boolean {
    const world = this.world;
    return world !== null && regionReady({ started: this.started, drawn: world.picture.drawnOrder.length, pending: world.picture.pending });
  }

  /**
   * Reads the time since the press of a zoom out that landed at Region once its picture can be shown.
   */
  private noteShown(): void {
    if (this.waitFrom !== null && this.regionIsReady()) {
      this.waitMs = this.now() - this.waitFrom;
      this.waitFrom = null;
    }
  }

  /**
   * Takes the minimap's step when its own zoom has moved it, so the Map's Nearby follows the one value.
   */
  private syncStep(): void {
    const step = this.nearby?.step();
    if (step === undefined || step === this.levels.step) {
      return;
    }
    this.levels = { ...this.levels, step };
    this.refresh();
  }

  /**
   * Zooms out one level of the ladder (decision 41): a smaller Nearby scale, then Region centred on the player and
   * following, whose picture is started the first time. It does nothing while the window is shut or at the farthest
   * built level.
   */
  public zoomOut(): void {
    this.zoomTo(zoomOut);
  }

  /**
   * Zooms in one level of the ladder: from Region to Nearby on the player whatever the view was dragged to, then a
   * larger Nearby scale up to the closest. It does nothing while the window is shut or at the closest.
   */
  public zoomIn(): void {
    this.zoomTo(zoomIn);
  }

  /**
   * Takes one step of the ladder.
   *
   * @param move `zoomOut` or `zoomIn` of the levels.
   */
  private zoomTo(move: (state: LevelState) => LevelState): void {
    if (!this.opened || this.nearby === null) {
      return;
    }
    const from = this.levels;
    const to = move({ ...from, step: this.nearby.step() });
    if (to.level === from.level && to.step === from.step) {
      return;
    }
    this.levels = to;
    if (to.level !== from.level) {
      this.panned = null;
    }
    if (to.step !== from.step) {
      this.nearby.setStep(to.step);
    }
    if (to.level === "region" && from.level !== "region") {
      this.waitFrom = this.now();
      this.waitMs = -1;
      this.showRegion();
      this.noteShown();
    }
    this.refresh();
    this.drawView();
  }

  /**
   * Opens the Map as the Map button does (a Map already open is brought forward) and shows it at Region centred on a
   * point, not following the character until Centre. It drops no pin and moves nothing in the world.
   *
   * @param point The point in tiles to centre on.
   */
  public openAt(point: TilePoint): void {
    this.open();
    const world = this.world;
    if (world === null) {
      return;
    }
    const from = this.levels;
    this.levels = { level: "region", step: this.nearby?.step() ?? from.step, follow: false, regionShown: true };
    this.panned = viewOrigin({ x: point.x, y: point.y }, this.view, this.pictureSize);
    if (from.level !== "region") {
      this.waitFrom = this.now();
      this.waitMs = -1;
      this.showRegion();
      this.noteShown();
    }
    this.refresh();
    this.drawView();
  }

  /**
   * Shuts the window.
   */
  public close(): void {
    if (!this.opened) {
      return;
    }
    this.opened = false;
    this.refresh();
  }

  /**
   * Opens the window if shut, shuts it if open.
   */
  public toggle(): void {
    if (this.opened && this.presented.shown === "popped") {
      this.bringForward();
    } else if (this.opened) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Tells the store whether the screen is the phone layout, which has the window alone: the float, kept or shown, is
   * drawn as the window there and comes back where the screen grows (ADR-100 decision 49).
   *
   * @param phone True for the phone layout.
   */
  public setPhone(phone: boolean): void {
    if (phone === this.phone) {
      return;
    }
    this.phone = phone;
    this.refresh();
  }

  /**
   * How the Map is shown on this screen: what was chosen, as the layout allows.
   *
   * @returns The presentation to draw.
   */
  private shownNow(): MapPresentation {
    return shownPresentation(this.presented.shown, this.phone);
  }

  /**
   * Shows the open Map as the float: the same store, level, pan, pin and places panel, drawn by the float instead of
   * the window (ADR-100 decision 49). The choice is kept on the device for the next open. It does nothing while the Map
   * is shut or already the float.
   */
  public float(): void {
    this.present(floatedState(this.presented));
  }

  /**
   * Shows the open Map as the window again (Dock), and keeps that choice. It does nothing while the Map is shut or
   * already the window.
   */
  public dock(): void {
    this.present(dockedState(this.presented));
  }

  /**
   * Shows the open Map as the popped out window (ADR-100 decision 50): the same store, level, pan, pin and places panel,
   * drawn by the pop-out into its own document. Nothing is kept on the device, since a window needs a gesture to open
   * (decision 34). It does nothing while the Map is shut, already popped out, or on the phone layout, which has no Pop out.
   */
  public popOut(): void {
    if (!this.opened || this.phone || this.presented.shown === "popped") {
      return;
    }
    this.presented = poppedOutState(this.presented);
    this.refresh();
  }

  /**
   * Returns the Map to the presentation last used, for Pop in and for the popped out window closing. It does nothing
   * unless the Map is open and popped out.
   */
  public popIn(): void {
    if (!this.opened || this.presented.shown !== "popped") {
      return;
    }
    this.presented = poppedInState(this.presented);
    this.refresh();
  }

  /**
   * Hears that the popped out window is asked to come forward: M and the Map button do that where a second map would
   * otherwise open (ADR-100 decisions 27 and 52).
   *
   * @param listener Called each time the window is asked to come forward.
   * @returns A function that stops listening.
   */
  public onBringForward(listener: () => void): () => void {
    this.forwarders.add(listener);
    return () => {
      this.forwarders.delete(listener);
    };
  }

  /**
   * Asks the popped out window to come forward, if the Map is popped out.
   */
  private bringForward(): void {
    if (this.opened && this.presented.shown === "popped") {
      for (const listener of [...this.forwarders]) {
        listener();
      }
    }
  }

  /**
   * Takes a presentation for the open Map, keeping the choice a person made.
   *
   * @param next The state wanted.
   */
  private present(next: PresentationState): void {
    if (!this.opened || this.presented.shown === "popped" || next.shown === this.presented.shown) {
      return;
    }
    this.presented = next;
    writeSetting(PRESENTATION_KEY, formatPresentation(next.kept), this.storage);
    this.refresh();
  }

  /**
   * Sets the character's feet in tiles and the way it heads. The page's copy of exploration is extended by the cells
   * the vision rectangle now covers, the pin clears when the feet come within its reach, and the window is told only
   * what changed.
   *
   * @param x Tiles across.
   * @param y Tiles down.
   * @param heading The heading in radians, 0 to the right and positive downward.
   */
  public setPosition(x: number, y: number, heading: number): void {
    this.px = x;
    this.py = y;
    this.heading = heading;
    this.placed = true;
    this.mark();
    this.ensurePin();
    if (this.pin !== null && pinReached(this.pin, { x, y })) {
      this.setPin(null);
    }
    if (this.opened) {
      this.refresh();
      this.drawView();
    }
  }

  /**
   * Draws the next chunk of the picture while the window is open, timed.
   */
  public frame(): void {
    const world = this.world;
    if (world === null || !this.opened || !this.started || this.levels.level !== "region") {
      return;
    }
    this.drawChunk(world);
    this.noteShown();
    this.refresh();
    this.drawView();
  }

  /**
   * Moves the view with a drag, which stops it following the character.
   *
   * @param dx The pointer's move across in logical pixels.
   * @param dy The pointer's move down in logical pixels.
   */
  public pan(dx: number, dy: number): void {
    const world = this.world;
    if (world === null || this.levels.level !== "region") {
      return;
    }
    const from = this.currentOrigin();
    this.panned = panOrigin(from, dx, dy, this.view, this.pictureSize);
    this.refresh();
    this.drawView();
  }

  /**
   * Returns the view to following the character.
   */
  public centre(): void {
    if (this.panned === null) {
      return;
    }
    this.panned = null;
    this.refresh();
    this.drawView();
  }

  /**
   * Drops the pin at a point, replacing any, and keeps it on the device.
   *
   * @param point The point in tiles.
   */
  public placePin(point: TilePoint): void {
    const world = this.world;
    if (world === null) {
      return;
    }
    this.ensurePin();
    this.setPin(pinAt(point, { width: world.width, height: world.height }));
    this.refresh();
  }

  /**
   * Pins a place of the list.
   *
   * @param key The place's key; anything the list does not hold changes nothing.
   */
  public pinPlace(key: string): void {
    const place = this.markList().find((entry) => entry.key === key);
    if (place !== undefined) {
      this.placePin({ x: place.x, y: place.y });
    }
  }

  /**
   * The words of one place: its name, distance and direction from the character, for a tooltip.
   *
   * @param key The place's key.
   * @returns The line, or null for a key no place holds.
   */
  public lineOf(key: string): string | null {
    const mark = this.markList().find((entry) => entry.key === key);
    return mark === undefined ? null : placeLine(measured(mark, { x: this.px, y: this.py }));
  }

  /**
   * How many times the panel's rows were built, which a panel left shut and a character standing in one tile never raise.
   *
   * @returns The count.
   */
  public get panelBuilds(): number {
    return this.builds;
  }

  /**
   * Opens the places panel and keeps that on the device.
   */
  public openPanel(): void {
    this.setPanel(true);
  }

  /**
   * Shuts the places panel, forgets its filter and keeps that on the device.
   */
  public closePanel(): void {
    this.setPanel(false);
  }

  /**
   * Opens the places panel if shut, shuts it if open.
   */
  public togglePanel(): void {
    this.setPanel(!this.panelOpen);
  }

  /**
   * What Escape does to the panel: clears the filter while there is one, else shuts the panel.
   */
  public escapePanel(): void {
    if (this.filter !== "") {
      this.setFilter("");
    } else {
      this.closePanel();
    }
  }

  /**
   * Sets the filter the panel narrows its rows by.
   *
   * @param text The text typed; kept to `FILTER_LENGTH` characters.
   */
  public setFilter(text: string): void {
    const next = text.slice(0, FILTER_LENGTH);
    if (next === this.filter) {
      return;
    }
    this.filter = next;
    this.refresh();
  }

  /**
   * Says which place's mark the pointer is on, so its name is drawn.
   *
   * @param key The place's key, or null when the pointer leaves.
   */
  public hoverMark(key: string | null): void {
    if (key === this.hover) {
      return;
    }
    this.hover = key;
    this.refresh();
  }

  /**
   * Says which place has keyboard focus, on its mark or on its row of the panel, so its name is drawn.
   *
   * @param key The place's key, or null when focus leaves.
   */
  public focusMark(key: string | null): void {
    if (key === this.focus) {
      return;
    }
    this.focus = key;
    this.refresh();
  }

  /**
   * Opens or shuts the panel.
   *
   * @param open The state wanted.
   */
  private setPanel(open: boolean): void {
    if (open === this.panelOpen && (open || this.filter === "")) {
      return;
    }
    this.panelOpen = open;
    if (!open) {
      this.filter = "";
      this.focus = null;
    }
    writeSetting(PLACES_PANEL_KEY, open ? "open" : "shut", this.storage);
    this.refresh();
  }

  /**
   * The pin kept for this character on this map, read from the device the first time it is wanted; the scene hands it
   * to the minimap each frame (ADR-100 decision 11, the compass's pip).
   *
   * @returns The pin in tiles, or null for none or before the map is known.
   */
  public pinNow(): TilePoint | null {
    this.ensurePin();
    return this.pin;
  }

  /**
   * Removes the pin.
   */
  public clearPin(): void {
    this.ensurePin();
    this.setPin(null);
    this.refresh();
  }

  /**
   * Gives the picture the exploration to draw and keeps the page's copy of it: the set the server sent when its counts
   * are the map's, else an empty one the page extends as the character walks.
   *
   * @param set The set sent, or null for none.
   */
  private adopt(set: ExploredSet | null): void {
    const world = this.world;
    if (world === null) {
      return;
    }
    const columns = fogCells(world.width);
    const rows = fogCells(world.height);
    this.explored = set !== null && set.columns === columns && set.rows === rows ? { columns, rows, cells: set.cells.slice() } : newExploredSet(columns, rows);
    this.last.fill(0);
    world.picture.setExplored(this.explored);
  }

  /**
   * Extends the page's copy by the cells the character's vision now covers, telling the picture which are new.
   */
  private mark(): void {
    const world = this.world;
    const set = this.explored;
    if (world === null || set === null) {
      return;
    }
    visionCells(Math.round(this.px * 16), Math.round(this.py * 16), set.columns, set.rows, this.next);
    const [a, b, c, d] = this.next;
    if (a === this.last[0] && b === this.last[1] && c === this.last[2] && d === this.last[3]) {
      return;
    }
    const fresh: FogCell[] = [];
    for (let row = b as number; row < (d as number); row += 1) {
      for (let column = a as number; column < (c as number); column += 1) {
        if (!isExplored(set, column, row)) {
          fresh.push([column, row]);
        }
      }
    }
    markVision(set, this.last, this.next);
    if (fresh.length > 0) {
      world.picture.explore(fresh);
    }
  }

  /**
   * The chunk under the character, or the map's middle chunk before it has a place.
   *
   * @param world The map.
   * @returns The chunk.
   */
  private chunkUnder(world: WorldMapWorld): ChunkRef {
    const x = this.placed ? this.px : world.width / 2;
    const y = this.placed ? this.py : world.height / 2;
    return { cx: Math.max(0, Math.min(Math.ceil(world.width / world.chunkSize) - 1, Math.floor(x / world.chunkSize))), cy: Math.max(0, Math.min(Math.ceil(world.height / world.chunkSize) - 1, Math.floor(y / world.chunkSize))) };
  }

  /**
   * The view's origin now: where it was dragged to, or centred on the character.
   *
   * @returns The origin.
   */
  private currentOrigin(): Origin {
    return this.panned ?? viewOrigin({ x: this.px, y: this.py }, this.view, this.pictureSize);
  }

  /**
   * Reads the pin kept for this character the first time it is wanted for the map and the character.
   */
  private ensurePin(): void {
    const world = this.world;
    if (world === null) {
      return;
    }
    const character = this.character();
    if (this.pinFor !== null && this.pinWorld === world.worldId && this.pinMap === world.mapId && this.pinCharacter === character) {
      return;
    }
    const key = pinKey(world.worldId, world.mapId, character);
    this.pinWorld = world.worldId;
    this.pinMap = world.mapId;
    this.pinCharacter = character;
    this.pinFor = key;
    this.pin = readSetting(key, (raw) => parsePin(raw, { width: world.width, height: world.height }), this.storage);
  }

  /**
   * Sets the pin and keeps it on the device; no pin is kept as an empty text.
   *
   * @param pin The pin, or null for none.
   */
  private setPin(pin: TilePoint | null): void {
    const world = this.world;
    this.pin = pin;
    if (world !== null && this.pinFor !== null) {
      writeSetting(this.pinFor, pin === null ? "" : serialisePin(pin), this.storage);
    }
  }

  /**
   * The marks of the places held, built once for each message.
   *
   * @returns The marks; none before a `mapPlaces`.
   */
  private markList(): readonly PlaceMark[] {
    const places = this.places;
    if (places === null && this.party === NO_PARTY) {
      return [];
    }
    if (this.markFor !== places || this.markParty !== this.party) {
      this.markFor = places;
      this.markParty = this.party;
      this.markCache = [...partyMarks(this.party.here), ...(places === null ? [] : placeMarks(places.places))];
      this.candidateCache = this.markCache.map((mark) => ({ key: mark.key, name: mark.name, x: mark.x, y: mark.y, size: markSize(mark.kind) }));
      this.alwaysNamed = new Set(this.party.here.map((member) => partyKey(member.place)));
    }
    return this.markCache;
  }

  /**
   * The candidates for names, built with the marks.
   *
   * @returns The candidates of the marks held.
   */
  private markedCandidates(): readonly NameCandidate[] {
    this.markList();
    return this.candidateCache;
  }

  /**
   * The words of every place, nearest first, for the report.
   *
   * @returns The lines.
   */
  private lines(): readonly string[] {
    return nearestFirst(this.markList(), { x: this.px, y: this.py }).map((place) => placeLine(place));
  }

  /**
   * The panel's rows, rebuilt only when it is shown and the places, the filter or the tile the character stands in
   * changed (distances are whole tiles).
   *
   * @returns The rows, or null while the window or the panel is shut or no places are known.
   */
  private panelModel(): PanelModel<KnownPlace> | null {
    const places = this.places;
    const party = this.party;
    if (!this.opened || this.levels.level !== "region" || !this.panelOpen || (places === null && party === NO_PARTY)) {
      return null;
    }
    const tx = Math.floor(this.px);
    const ty = Math.floor(this.py);
    const held = this.panelCache;
    if (held !== null && held.places === places && held.party === party && held.filter === this.filter && held.tx === tx && held.ty === ty) {
      return held.model;
    }
    const model = buildPanel([...nearestFirst(this.markList(), { x: this.px, y: this.py }), ...awayEntries(party.away)], this.filter, PLACE_KINDS);
    this.builds += 1;
    this.panelCache = { places, party, filter: this.filter, tx, ty, model };
    return model;
  }

  /**
   * Builds the snapshot for the state now.
   *
   * @returns The snapshot.
   */
  private build(): WorldMapSnapshot {
    const world = this.world;
    if (world === null) {
      return { open: this.opened, ready: false, presentation: this.shownNow(), level: this.levels.level, step: this.levels.step, canZoomIn: false, canZoomOut: false, regionReady: false, title: "", heading: "", mapLabel: "", following: true, drawing: false, view: this.view, origin: { left: 0, top: 0 }, you: null, youEdge: null, marks: [], named: [], count: 0, panelOpen: this.panelOpen, filter: this.filter, panel: null, pin: null, pinLine: null };
    }
    this.ensurePin();
    const origin = this.currentOrigin();
    const you = this.placed ? { x: this.px, y: this.py, heading: this.heading } : null;
    const pin = this.pin;
    const pinLine = pin === null ? null : say("worldMap.pinLine", { distance: say("worldMap.tiles", { count: Math.round(Math.hypot(pin.x - this.px, pin.y - this.py)) }), direction: say(`worldMap.direction.${directionOf(pin.x - this.px, pin.y - this.py)}`) });
    return {
      open: this.opened,
      ready: true,
      presentation: this.shownNow(),
      level: this.levels.level,
      step: this.levels.step,
      canZoomIn: this.nearby !== null && canZoomIn(this.levels),
      canZoomOut: this.nearby !== null && canZoomOut(this.levels),
      regionReady: this.regionIsReady(),
      title: world.title,
      heading: say("worldMap.heading", { name: world.title, level: say(`worldMap.level.${this.levels.level}`) }),
      mapLabel: say("worldMap.map", { name: world.title }),
      following: this.panned === null,
      drawing: this.levels.level === "region" && this.started && world.picture.pending > 0,
      view: this.view,
      origin,
      you,
      youEdge: you === null ? null : edgeMark({ x: you.x, y: you.y }, origin, this.view),
      marks: this.opened && this.levels.level === "region" ? this.markList() : NO_MARKS,
      named: this.opened && this.levels.level === "region" && (this.places !== null || this.party !== NO_PARTY) ? chooseNames(this.markedCandidates(), { x: this.px, y: this.py }, { hover: this.hover, focus: this.focus, pin }, undefined, this.alwaysNamed) : NO_NAMES,
      count: (this.places === null ? 0 : this.places.places.length) + this.party.here.length + this.party.away.length,
      panelOpen: this.panelOpen,
      filter: this.filter,
      panel: this.panelModel(),
      pin,
      pinLine: this.opened ? pinLine : null,
    };
  }

  /**
   * Rebuilds the snapshot and tells the listeners when it differs from the last.
   */
  private refresh(): void {
    this.ensurePin();
    if (this.unchanged()) {
      return;
    }
    const next = this.build();
    if (sameSnapshot(next, this.snapshot)) {
      return;
    }
    this.snapshot = next;
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  /**
   * Whether the inputs the snapshot is built from read the same as at the last build, the position compared to a
   * sixteenth of a tile and the heading to an eighth of a radian, so a frame with nothing new builds nothing. The
   * stamp is taken when this is false.
   *
   * @returns True when the snapshot would be as it is.
   */
  private unchanged(): boolean {
    const world = this.world;
    const x = Math.round(this.px * 16);
    const y = Math.round(this.py * 16);
    const heading = Math.round(this.heading * 8);
    const drawing = world !== null && this.levels.level === "region" && this.started && world.picture.pending > 0;
    const ready = this.regionIsReady();
    const was = this.stamp;
    if (was !== null && was.world === world && was.opened === this.opened && was.presentation === this.shownNow() && was.started === this.started && was.drawing === drawing && was.ready === ready && was.level === this.levels.level && was.step === this.levels.step && was.view === this.view && was.panned === this.panned && was.x === x && was.y === y && was.heading === heading && was.placed === this.placed && was.places === this.places && was.party === this.party && was.pin === this.pin && was.panelOpen === this.panelOpen && was.filter === this.filter && was.hover === this.hover && was.focus === this.focus) {
      return true;
    }
    this.stamp = { world, opened: this.opened, presentation: this.shownNow(), started: this.started, drawing, ready, level: this.levels.level, step: this.levels.step, view: this.view, panned: this.panned, x, y, heading, placed: this.placed, places: this.places, party: this.party, pin: this.pin, panelOpen: this.panelOpen, filter: this.filter, hover: this.hover, focus: this.focus };
    return false;
  }

  /**
   * Asks the view to repaint when the picture's pixels or the view's origin changed since it last did, while open.
   */
  private drawView(): void {
    const world = this.world;
    if (world === null || !this.opened || this.levels.level !== "region") {
      return;
    }
    const origin = this.currentOrigin();
    const version = world.picture.version;
    if (version === this.drawnVersion && origin.left === this.drawnOrigin.left && origin.top === this.drawnOrigin.top) {
      return;
    }
    this.drawnVersion = version;
    this.drawnOrigin = origin;
    this.drawCount += 1;
    for (const drawer of [...this.drawers]) {
      drawer();
    }
  }
}
